// Importing from the chat — T83 (22 Sep 2026), agents doc § 7.6 item 6.
//
// THE DIALOG'S LANE, ENTERED FROM THE PANEL. The recruiter attaches a list to what they were going
// to ask anyway — "these are prospects for Sales" — and kadmon works out which importer it is and
// what the dialog's first step would have said (the tab, the country, the client and role for rows
// naming none), reads the file with `preview_import`, and the answer comes back with the import's
// own preview on it. This module is the part with rules in it, as `importFile.ts` is for the
// dialog: the file into the body's shape, the preview into the sentences the card shows, and the
// preview into the rows the tap files.
//
// THE FILE GOES UP ONCE, WITH THE QUESTION, and is stored nowhere by anybody: not on a card (no
// card carries a file's rows), not in a table, not on the brain's side at all
// (services/bff/src/attachment.ts says why in full). What the tap sends is the ROWS the preview
// gave — the dialog's own confirm body, which its route re-reads row by row. What lives in this
// tab is the PREVIEW, so the tab that asked is the tab that can import, and a reload loses an
// unconfirmed one exactly as it does in the dialog.
//
// WHAT IT DOES NOT DO: decide anything about a row. Which rows are ready, which are refused and
// why, which person is already on file, which client or role a row lands on — all of it is the
// service's preview, drawn here as it arrived.
import type {
  ClientImportPreview,
  ClientImportRowFields,
  ImportPreview,
  ImportProposal,
  ImportRowFields,
  ImportSettlement,
  ProblemRow,
  ClientProblemRow,
} from '@vera/bff';
import { companyImportRow, isWorkbook, personImportRow, toBase64 } from './importFile.ts';

/**
 * What the attach control takes. T83: the two the importers read. T154a (28 Sep 2026): and every
 * file kadmon reads — a PDF, a Word document, a photo or a screenshot, a text file — which is read
 * in the question's request by the recruiters' reading (services/bff/src/uploads.ts) and kept by
 * nobody; what she read stays in the chat.
 */
export const ATTACH_ACCEPT = [
  '.csv',
  '.xlsx',
  '.pdf',
  '.docx',
  '.txt',
  '.md',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.mp4',
  '.mov',
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
].join(',');

/** T154a: a list the importers read (T83), rather than a file kadmon reads. */
export const isSpreadsheet = (name: string): boolean => /\.(csv|xlsx)$/i.test(name);

/** T154a: a photo or a screenshot, by its name or its type. */
const isImage = (file: Pick<File, 'name' | 'type'>): boolean =>
  /\.(jpe?g|png|gif|webp)$/i.test(file.name) || file.type.startsWith('image/');

/**
 * The biggest file the service will read — `IMPORT_MAX_BYTES`, restated here as a NUMBER rather
 * than imported, because this app type-imports from `@vera/bff` and never takes a value from it:
 * a value import would pull the service into the browser's bundle. `importChat.test.ts` holds the
 * two equal, so the cap cannot drift without the suite saying so.
 */
export const ATTACH_MAX_BYTES = 2 * 1024 * 1024;

/**
 * T154a: the biggest document and photo the service reads — `DOCUMENT_MAX_BYTES` and
 * `IMAGE_MAX_BYTES` in services/bff/src/uploads.ts, restated as numbers for the reason above, and
 * held equal to them by importChat.test.ts.
 */
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** T154a: the most this file may weigh, by what it is. */
export const attachMaxBytes = (file: Pick<File, 'name' | 'type'>): number =>
  isSpreadsheet(file.name)
    ? ATTACH_MAX_BYTES
    : isImage(file)
      ? IMAGE_MAX_BYTES
      : DOCUMENT_MAX_BYTES;

/**
 * The file as the find's body carries it — the same shape both preview routes take. T154a: or any
 * other file, as `document` in base64 with the type the browser declared.
 */
export interface AskAttachment {
  name: string;
  text?: string;
  workbook?: string;
  document?: string;
  type?: string;
}

/**
 * A chosen file, read in this browser. A workbook goes as base64 (the service reads the zip), a
 * CSV as text. `maxBytes` is the service's own cap, so a file too big is said here rather than
 * refused after the upload.
 */
export async function readAttachment(
  file: File,
  maxBytes: number = attachMaxBytes(file),
): Promise<AskAttachment | { tooBig: true }> {
  if (file.size > maxBytes) return { tooBig: true };
  if (isWorkbook(file.name)) {
    return { name: file.name, workbook: toBase64(new Uint8Array(await file.arrayBuffer())) };
  }
  if (isSpreadsheet(file.name)) return { name: file.name, text: await file.text() };
  // T154a: anything else goes as it is, for the service to read.
  return {
    name: file.name,
    document: toBase64(new Uint8Array(await file.arrayBuffer())),
    ...(file.type ? { type: file.type } : {}),
  };
}

/**
 * T360 E3 3b: the attached file's bytes in base64, as the service hashed them: a document or a
 * workbook as read, a CSV as its text's UTF-8 (the service's `attachedOf`). Sent again on the tap
 * that confirms a Drive card saving it, since the brain keeps no file.
 */
export function attachmentBase64(file: AskAttachment): string {
  return file.document ?? file.workbook ?? toBase64(new TextEncoder().encode(file.text ?? ''));
}

/** Bytes as the chip says them — a recruiter checking they attached the right list. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The card's heading: which importer this is, and the file it read. */
export const importCardTitle = (proposal: ImportProposal): string =>
  proposal.kind === 'people'
    ? 'Import people from a file'
    : proposal.kind === 'companies'
      ? 'Import companies from a file'
      : 'Import LinkedIn connections';

/**
 * What the import would take — the rows its Import button counts. For a LinkedIn network (T111)
 * that is the new connections and the ones refreshed; nobody the brain already has is among them.
 */
export const importReady = (proposal: ImportProposal): number =>
  proposal.kind === 'connections'
    ? proposal.preview.add + proposal.preview.update
    : proposal.preview.summary.ready;

/** How many rows the import will not take, whichever importer it is. */
const problemCount = (proposal: ImportProposal): number =>
  proposal.kind === 'connections' ? proposal.preview.problems : proposal.preview.summary.problems;

/**
 * The lines the card shows above the rows — every number the recruiter decides on, and nothing
 * they would have to work out. The service's own summary, said in the brain's words.
 */
export function importCardLines(proposal: ImportProposal): string[] {
  const lines: string[] = [];
  const many = (n: number, one: string, more = `${one}s`) => `${n} ${n === 1 ? one : more}`;
  if (proposal.kind === 'connections') {
    // T111: a whole network — what is added, what is left alone because the brain has them, and
    // what the file no longer lists. Every number is the plan the tap makes again at the tap.
    const network = proposal.preview;
    lines.push(
      `${many(network.add, 'person', 'people')} new to the brain, of ${network.total} in the file`,
    );
    if (network.update > 0) {
      lines.push(`${many(network.update, 'connection')} already on the tab, refreshed`);
    }
    if (network.skipped > 0) {
      lines.push(
        `${many(network.skipped, 'person', 'people')} already in the brain, left where they are`,
      );
    }
    if (network.remove > 0) {
      lines.push(`${many(network.remove, 'connection')} no longer in your network, removed`);
    }
    if (network.release > 0) {
      lines.push(
        `${many(network.release, 'connection')} no longer yours: kept, because they are somebody else’s too or were added by hand`,
      );
    }
  } else if (proposal.kind === 'people') {
    const people = proposal.preview.summary;
    lines.push(`${many(people.ready, 'person', 'people')} to import, of ${people.rows} rows`);
    if (people.existing > 0) {
      lines.push(`${many(people.existing, 'row')} already on file: their record is updated`);
    }
    if (people.newClients.length > 0) {
      lines.push(`New client companies: ${people.newClients.join(', ')}`);
    }
    if (people.newRoles.length > 0) {
      lines.push(
        `New roles: ${people.newRoles
          .map((role) => `${role.title}${role.client ? ` at ${role.client}` : ''}`)
          .join(', ')}`,
      );
    }
    if (proposal.country) lines.push(`Numbers with no country code read as ${proposal.country}`);
    if (proposal.defaults.client || proposal.defaults.role) {
      lines.push(
        `Rows naming none: ${[proposal.defaults.client, proposal.defaults.role]
          .filter(Boolean)
          .join(' · ')}`,
      );
    }
  } else {
    const companies = proposal.preview.summary;
    lines.push(
      `${many(companies.ready, 'company', 'companies')} to import, of ${companies.rows} rows`,
    );
    if (companies.creating > 0)
      lines.push(`${many(companies.creating, 'new company', 'new companies')}`);
    if (companies.updating > 0)
      lines.push(`${many(companies.updating, 'row')} already on this tab, updated`);
    if (companies.elsewhere > 0) {
      lines.push(
        `${many(companies.elsewhere, 'row')} on another tab: updated where they are, never moved`,
      );
    }
    if (companies.contacts > 0) lines.push(`${many(companies.contacts, 'contact')}`);
  }
  // Who, right under how many — the first thing a recruiter checks is that the right list came.
  const { names, more } = readyNames(proposal);
  if (names.length > 0) {
    lines.splice(1, 0, `${names.join(', ')}${more > 0 ? `, and ${more} more` : ''}`);
  }
  const problems = problemCount(proposal);
  if (problems > 0) {
    lines.push(`${many(problems, 'row')} the import will not take, each with its reason`);
  }
  return lines;
}

/**
 * WHO IS IN THE FILE — the first few by name, and how many more. The task asks the card to show
 * the rows, and a count alone does not: a recruiter checking that the right list was attached
 * reads names. Every row is not the chat's job — a file runs to 1,500 — and the card says where to
 * go to read them one by one.
 */
export function readyNames(proposal: ImportProposal, shown = 6): { names: string[]; more: number } {
  if (proposal.kind === 'connections') {
    // The preview names the first two hundred it adds; the count is the whole of them.
    const names = proposal.preview.addShown.slice(0, shown).map((row) => row.name);
    return { names, more: Math.max(0, proposal.preview.add - names.length) };
  }
  const all =
    proposal.kind === 'people'
      ? proposal.preview.ready.map((row) => row.name).filter((name): name is string => !!name)
      : proposal.preview.ready.map((row) => row.name);
  return { names: all.slice(0, shown), more: Math.max(0, all.length - shown) };
}

/** Every refused row, as the card lists them: where it is, who it is, and the import's sentence. */
export function refusedRows(
  proposal: ImportProposal,
): { key: string; where: string; name: string | null; reason: string }[] {
  if (proposal.kind === 'connections') {
    return proposal.preview.problemsShown.map((row) => ({
      key: `\u0000${row.row}`,
      where: `Row ${row.row}`,
      name: row.name,
      reason: row.reason,
    }));
  }
  const rows: readonly (ProblemRow | ClientProblemRow)[] = proposal.preview.problems;
  const sheets = proposal.preview.summary.sheets.length;
  return rows.map((row) => ({
    key: `${row.sheet}\u0000${row.row}`,
    where: sheets > 1 && row.sheet ? `Row ${row.row} on ${row.sheet}` : `Row ${row.row}`,
    name: row.name,
    reason: row.reason,
  }));
}

/**
 * The rows the tap files — the preview's ready rows through the dialog's own mapper.
 *
 * NO SWITCHES HERE, ON PURPOSE. The dialog offers "create as new instead" per close match, which
 * is a decision about a NAME and belongs where a recruiter is reading the names one by one. In the
 * chat a close match is taken as the match, exactly as the dialog's default does, and the card
 * says which rows those were so a recruiter who wants to decide each one opens the dialog instead.
 */
export const peopleRows = (preview: ImportPreview): ImportRowFields[] =>
  preview.ready.map((ready) => personImportRow(ready, new Set(), new Set()));

export const companyRows = (preview: ClientImportPreview): ClientImportRowFields[] =>
  preview.ready.map((ready) => companyImportRow(ready, new Set()));

/**
 * T113a (25 Sep 2026): whether the import would add anything new — a client, a role, a company, or
 * a contact beside the ones a company already has. The card then says where to point one at what
 * the brain already has: the dialog offers that, name by name, and the card does not.
 */
export function addsNew(proposal: ImportProposal): boolean {
  if (proposal.kind === 'people') {
    const { summary } = proposal.preview;
    return summary.newClients.length > 0 || summary.newRoles.length > 0;
  }
  if (proposal.kind === 'companies') {
    return (
      proposal.preview.summary.creating > 0 || Object.keys(proposal.preview.contactsAt).length > 0
    );
  }
  return false;
}

/**
 * The close matches the card names, so nothing is matched silently: a client, a role or a company
 * one edit or one token from a name already on file (the preview flags each `close`).
 */
export function closeMatches(proposal: ImportProposal): string[] {
  if (proposal.kind === 'connections') {
    // T111: a name match holds the person back unless a recruiter says they are somebody else —
    // a decision made one by one in the tab's own import, which is why the card only names them.
    return proposal.preview.flagged.map(
      (row) => `${row.name}${row.company ? ` (${row.company})` : ''} ~ ${row.match.where}`,
    );
  }
  if (proposal.kind === 'companies') {
    return [
      ...new Set(
        proposal.preview.ready
          .filter((row) => row.match?.closeness === 'close')
          .map((row) => `${row.name} → ${row.match!.name}`),
      ),
    ];
  }
  const names = new Set<string>();
  for (const row of proposal.preview.ready) {
    if (row.client?.match?.closeness === 'close') {
      names.add(`${row.client.name} → ${row.client.match.name}`);
    }
    if (row.role?.match?.closeness === 'close') {
      names.add(`${row.role.name} → ${row.role.match.name}`);
    }
  }
  return [...names];
}

/**
 * What the settle poll asks about, after the people import has filed its requests: one item per
 * request, carrying the role and note that row is waiting to have recorded. T43's own shape — the
 * worker writes the record, and the service places the person as it answers.
 */
export function settlements(
  filed: readonly { sheet: string | null; row: number; entryId: string; searchId: string | null }[],
  rows: readonly ImportRowFields[],
): ImportSettlement[] {
  const byRow = new Map(rows.map((row) => [`${row.sheet}\u0000${row.row}`, row] as const));
  return filed.map((request) => {
    const row = byRow.get(`${request.sheet}\u0000${request.row}`);
    return {
      entryId: request.entryId,
      searchId: request.searchId,
      stage: row?.stage ?? null,
      // T133: a stage the file gave moves a person already on the role, or takes them off.
      stageGiven: row?.stageGiven ?? false,
      note: row?.note ?? null,
    };
  });
}
